'use client';

import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface Language {
  locale: string;
  name: string;
  flag: string;
}

/*interface Currency {
  code: string;
  symbol: string;
  name: string;
  flag: string;
}*/

const LANGUAGES: Language[] = [
  { locale: 'en', name: 'English', flag: '🇬🇧' },
  { locale: 'se', name: 'Svenska', flag: '🇸🇪' },
  { locale: 'fr', name: 'Français', flag: '🇫🇷' },
  { locale: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { locale: 'it', name: 'Italiano', flag: '🇮🇹' },
  { locale: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { locale: 'es', name: 'Español', flag: '🇪🇸' },
  { locale: 'pl', name: 'Polski', flag: '🇵🇱' },
  { locale: 'cz', name: 'Český', flag: '🇨🇿' },
  { locale: 'gr', name: 'ελληνικά', flag: '🇬🇷' },
  { locale: 'dk', name: 'Dansk', flag: '🇩🇰' },
];

/*const CURRENCIES: Currency[] = [
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', flag: '🇸🇪' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Złoty', flag: '🇵🇱' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', flag: '🇳🇿' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', flag: '🇳🇴' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', flag: '🇩🇰' }
];*/

// Path translation cache
const pathTranslationCache: Record<string, string> = {};

interface LocaleCurrencyChangerProps {
  direction?: 'up' | 'down';
  closeMenu?: () => void;
  isLocaleChanger?: boolean;
}

export default function LocaleCurrencyChanger({ 
  direction = 'down',
  closeMenu
}: LocaleCurrencyChangerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  //const { preferredCurrency, getActiveCurrency, setPreferredCurrency, clearPreferredCurrency } = useCurrencyStore();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [mounted, setMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('languageCurrencyChanger')

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!mounted) {
    return (
      <div className="w-20 h-10 rounded-lg bg-bg-main/20 backdrop-blur-sm animate-pulse" />
    );
  }

  const currentLanguage = LANGUAGES.find(lang => lang.locale === locale) || LANGUAGES[0];
  //const activeCurrency = getActiveCurrency(locale);
  //const selectedCurrency = CURRENCIES.find(c => c.code === activeCurrency);
  //const localeDefaultCurrency = getCurrencyFromLocale(locale);

  const switchLocale = async (newLocale: string) => {
    if (newLocale === locale || !pathname) return;
    
    try {
      setIsLoading(prev => ({ ...prev, [newLocale]: true }));
      
      const cacheKey = `${locale}-${newLocale}-${pathname}`;
      
      let translatedPath;
      if (pathTranslationCache[cacheKey]) {
        translatedPath = pathTranslationCache[cacheKey];
      } else {
        const response = await fetch(
          `/api/translate-path?currentLocale=${locale}&targetLocale=${newLocale}&pathname=${encodeURIComponent(pathname)}`
        );
        
        const data = await response.json();
        translatedPath = data.translatedPath;
        console.log("Translate Path API Response: ", translatedPath)
        pathTranslationCache[cacheKey] = translatedPath;
      }
      
      router.push(translatedPath);
      
      // Close the dropdown and then the mobile menu if the callback is provided
      setIsOpen(false);
      if (closeMenu) {
        closeMenu();
      }
    } catch {
      router.push(`/${newLocale}`);
    } finally {
      setIsLoading(prev => ({ ...prev, [newLocale]: false }));
    }
  };

  /*const handleCurrencyChange = (selectedCurrencyCode: string) => {
    if (selectedCurrencyCode === localeDefaultCurrency) {
      clearPreferredCurrency();
    } else {
      setPreferredCurrency(selectedCurrencyCode);
    }
  };*/

  return (
    <div
      ref={dropdownRef}
      className="relative inline-block text-left"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Main button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg bg-bg-main/20 backdrop-blur-sm border border-text-main/10 hover:bg-bg-main/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
        title={`${currentLanguage.name}`} //- ${selectedCurrency?.name || activeCurrency}
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        {/*<span className="text-sm font-medium text-text-main">
          {selectedCurrency?.symbol || activeCurrency}
        </span>*/}
        <svg 
          className={`w-4 h-4 text-text-secondary transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
                 <div
           className={`${
             direction === 'up'
               ? 'absolute bottom-full mb-2 left-0'
               : 'absolute top-full mt-2 right-0'
           } w-72 rounded-lg shadow-lg bg-bg-main/90 backdrop-blur-xl border border-border-color ring-1 ring-primary/5 z-50`}
         >
                     {/* Languages Section */}
           <div className="p-3 border-b border-border-color">
             <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                {t('language')}
             </h3>
            <div className="space-y-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.locale}
                  onClick={() => switchLocale(lang.locale)}
                  disabled={isLoading[lang.locale]}
                                     className={`flex items-center w-full px-3 py-2 text-sm rounded-md transition-colors ${
                     lang.locale === locale
                       ? 'bg-primary/20 text-primary'
                       : 'text-text-main hover:bg-bg-main/50'
                   } disabled:opacity-50`}
                >
                  <span className="mr-3 text-lg">{lang.flag}</span>
                  <span className="flex-1 text-left">{lang.name}</span>
                  {isLoading[lang.locale] && (
                    <div className="ml-2 w-4 h-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
                  )}
                  {lang.locale === locale && (
                    <div className="ml-2 w-2 h-2 bg-primary rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Currencies Section

          <div className="p-3">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
              Currency
            </h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {CURRENCIES.map((currency) => {
                const isSelected = currency.code === activeCurrency;
                const isDefault = currency.code === localeDefaultCurrency && !preferredCurrency;
                
                return (
                  <button
                    key={currency.code}
                    onClick={() => handleCurrencyChange(currency.code)}
                                         className={`flex items-center w-full px-3 py-2 text-sm rounded-md transition-colors ${
                       isSelected
                         ? 'bg-primary/20 text-primary'
                         : 'text-text-main hover:bg-bg-main/50'
                     }`}
                  >
                    <span className="mr-3 text-lg">{currency.flag}</span>
                    <div className="flex-1 text-left">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{currency.code}</span>
                        <span className="text-text-secondary">{currency.symbol}</span>
                                                 {isDefault && (
                           <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                             Auto
                           </span>
                         )}
                      </div>
                      <div className="text-xs text-text-secondary">{currency.name}</div>
                    </div>
                    {isSelected && (
                      <div className="ml-2 w-2 h-2 bg-primary rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>

          </div>*/}
        </div>
      )}
    </div>
  );
} 